import { useState, type ReactNode } from 'react';
import { BaseCard, SideListCard } from '@practium/ui';
import type { PlanGeneralForm } from '../../../schemas/parameters.schema';
import type { FertilizationPlanReferenceDTO, PlanSectionProps } from '../../../types/parameters.types';
import { PlanFormModal } from '../../Plan/PlanFormModal';
import styles from '../../Sections/Sections.module.css';
import tableStyles from '../../Tables/ParamsTable.module.css';

interface PlanSidebarProps {
	plans: FertilizationPlanReferenceDTO[];
	plan: FertilizationPlanReferenceDTO | null;
	isLoading: boolean;
	isSaving: boolean;
	isCreateModalOpen: boolean;
	viewSwitch: ReactNode;
	onSelect: (id: string) => void;
	onSave: PlanSectionProps['onSave'];
	onCreate: (form: PlanGeneralForm, base: FertilizationPlanReferenceDTO) => Promise<boolean>;
	onActivate: (plan: FertilizationPlanReferenceDTO) => Promise<boolean>;
	onDelete: (plan: FertilizationPlanReferenceDTO) => Promise<boolean>;
	onCloseCreateModal: () => void;
}

export const PlanSidebar = ({
	plans,
	plan,
	isLoading,
	isSaving,
	isCreateModalOpen,
	viewSwitch,
	onSelect,
	onSave,
	onCreate,
	onActivate,
	onDelete,
	onCloseCreateModal,
}: PlanSidebarProps) => {
	const [isEditOpen, setIsEditOpen] = useState(false);
	const closeEdit = () => setIsEditOpen(false);

	const runAndClose = async (action: (target: FertilizationPlanReferenceDTO) => Promise<boolean>) => {
		if (plan && (await action(plan))) closeEdit();
	};

	return (
		<>
			<div className={styles.sidebarList}>
				<SideListCard
					stretch
					title="Planos de cálculo"
					subtitle="Só o plano ativo é usado no cálculo"
					items={plans.map((item) => ({
						id: item._id,
						label: item.name,
						sublabel: `Versão ${item.version}`,
						badge: item.isActive ? 'Ativo' : undefined,
					}))}
					activeId={plan?._id}
					onSelect={onSelect}
					filterSlot={viewSwitch}
					actionButton={{ label: 'Editar', onClick: () => setIsEditOpen(true), disabled: !plan }}
					isLoading={isLoading}
				/>
			</div>

			{plan && (
				<div className={styles.sidebarMapSlot}>
					<BaseCard
						stretch
						title="Detalhes do plano"
						subtitle="Dados gerais"
					>
						{
							<dl className={styles.planInfo}>
								<div className={styles.planInfoItem}>
									<dt className={styles.planInfoLabel}>Status</dt>
									<dd className={styles.planInfoValue}>
										<span
											className={`${tableStyles.badge} ${plan.isActive ? tableStyles.badgeHigh : tableStyles.badgeMedium}`}
										>
											{plan.isActive ? 'Ativo' : 'Inativo'}
										</span>
									</dd>
								</div>
								<div className={styles.planInfoItem}>
									<dt className={styles.planInfoLabel}>Descrição</dt>
									<dd className={styles.planInfoValue}>{plan.description || '—'}</dd>
								</div>
							</dl>
						}
					</BaseCard>
				</div>
			)}

			{isCreateModalOpen && (
				<PlanFormModal plans={plans} plan={null} isSaving={isSaving} onSubmit={onCreate} onClose={onCloseCreateModal} />
			)}

			{isEditOpen && plan && (
				<PlanFormModal
					plans={plans}
					plan={plan}
					isSaving={isSaving}
					onSubmit={(form) => onSave(form, 'Plano atualizado com sucesso!')}
					onActivate={plan.isActive ? undefined : () => runAndClose(onActivate)}
					onDelete={plan.isActive ? undefined : () => runAndClose(onDelete)}
					onClose={closeEdit}
				/>
			)}
		</>
	);
};
